import React, { useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { Container, Typography, Button, Box, CircularProgress, IconButton, Popover, Tooltip } from "@material-ui/core"
import CancelIcon from "@material-ui/icons/Cancel"
import useStyles from "./dropZoneStyles"
import RenderFileList from "./RenderFileList"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"
import SupportedFileList from "./SupportedFileList"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

const MAX_UPLOAD_SIZE = 104857600 //100MB

const DropZone = ({ onSubmit, onCancel }) => {
	const classes = useStyles()
	const [t] = useTranslation("global")

	const acceptedFilesFormats = useSelector((state) => state.upload.supportedFormats)

	const [uploadDetails, setUploadDetails] = useState({ fileLength: 0, fileSize: 0 })
	const [selectedFiles, setSelectedFiles] = useState([])
	const [rejectedFiles, setRejectedFiles] = useState([])
	const [selectionInProgress, setSelectionInProgress] = useState(false)

	const [anchorEl, setAnchorEl] = useState(null)

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => {
		setAnchorEl(null)
	}

	const open = Boolean(anchorEl)
	const id = open ? "file-type-support-popover" : undefined

	const getFileDetails = (prev, files) => {
		let totalFileSize = prev?.fileSize || 0
		files.forEach((file) => {
			totalFileSize += file.size
		})
		return { fileLength: files.length, fileSize: totalFileSize }
	}

	const calculateRemainingSize = () => {
		const remainingSize = MAX_UPLOAD_SIZE - uploadDetails.fileSize
		return remainingSize
	}

	const onDrop = useCallback(
		(acceptedFiles) => {
			setSelectionInProgress(true)
			const acceptedFilesList = acceptedFiles.filter(
				(file) =>
					acceptedFilesFormats.includes(file.name.split(".").pop().toLowerCase()) &&
					!selectedFiles.some((sFile) => sFile.name === file.name)
			)
			const rejectedFilesList = acceptedFiles.filter(
				(file) =>
					!acceptedFilesFormats.includes(file.name.split(".").pop().toLowerCase()) &&
					!rejectedFiles.some((sFile) => sFile.name === file.name)
			)
			const fileDetails = getFileDetails(uploadDetails, acceptedFilesList)

			if (!_.isEmpty(fileDetails)) {
				setSelectedFiles((prevFiles) => [...prevFiles, ...acceptedFilesList]) // Append new files to the existing list
				setUploadDetails(fileDetails)
			}

			if (!_.isEmpty(rejectedFilesList)) {
				setRejectedFiles((prevFiles) => [...prevFiles, ...rejectedFilesList])
			}
			setSelectionInProgress(false)
		},
		[uploadDetails]
	)

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: acceptedFilesFormats, // Make sure acceptedFilesFormats is defined
		maxSize: MAX_UPLOAD_SIZE, // 100 MB in bytes
	})

	const handleUpload = () => {
		if (selectedFiles.length > 0) {
			onSubmit(selectedFiles)
		}
	}

	const handleDelete = (filename) => {
		setUploadDetails((prevDetails) => ({
			...prevDetails,
			fileSize: prevDetails.fileSize - selectedFiles.find((file) => file.name === filename).size,
		}))
		setSelectedFiles((prevFiles) => prevFiles.filter((file) => file.name !== filename))
	}

	const handleClear = () => {
		setUploadDetails({ fileLength: 0, fileSize: 0 })
		setSelectedFiles([])
		setRejectedFiles([])
		onCancel()
	}

	const remainingSize = (calculateRemainingSize() / 1048576).toFixed(2)

	return (
		<>
			<Container className={classes.dropZoneContainer}>
				<div {...getRootProps()} className={classes.root}>
					{selectionInProgress ? (
						<CircularProgress />
					) : (
						<div>
							<input {...getInputProps()} />
							<Typography className={classes.textBlack}>
								<span className={classes.textBlue}>{t("dropZone.text1")}</span> {t("dropZone.text2")}
							</Typography>
							<Typography>
								{selectedFiles.length > 0
									? calculateRemainingSize() <= 0
										? t("dropZone.messageExceed")
										: t("dropZone.messageRemaining", { remainingSize })
									: t("dropZone.messageMaximum")}
							</Typography>
						</div>
					)}
				</div>

				{selectedFiles.length > 0 && (
					<Box className={classes.fileList}>
						<Typography variant="body2" style={{ fontWeight: 500 }}>
							{t("dropZone.acceptedFiles")}: <span>({selectedFiles.length})</span>
						</Typography>
						<RenderFileList files={selectedFiles} handleDelete={handleDelete} isDelete={true} />
					</Box>
				)}

				{rejectedFiles.length > 0 && (
					<Box>
						<Typography variant="body2" style={{ fontWeight: 500, marginBottom: "8px" }}>
							{t("dropZone.unsupportedFiles")}: <span>({rejectedFiles.length})</span>
						</Typography>
						<RenderFileList files={rejectedFiles} handleDelete={handleDelete} isDelete={false} />
					</Box>
				)}
			</Container>

			<hr className={classes.dividerLine} />
			<div className={`${classes.row} ${classes.buttonWrapper}`}>
				<p>
					<span style={{ fontWeight: 500 }}>{t("dropZone.supportedTypes")}</span>{" "}
					<InfoOutlinedIcon fontSize="small" aria-describedby={id} onClick={handleClick} style={{ cursor: "pointer" }} />
					<Popover
						id={id}
						open={open}
						anchorEl={anchorEl}
						onClose={handleClose}
						anchorOrigin={{
							vertical: "top",
							horizontal: "center",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "center",
						}}
					>
						<SupportedFileList />
					</Popover>
				</p>
				<div style={{ display: "flex", gap: "8px" }}>
					<Button className={`${classes.button} ${classes.btnCancel}`} onClick={handleClear}>
						{t("dropZone.btnCancel")}
					</Button>
					<Tooltip
						title={
							selectedFiles.length === 0
								? t("dropZone.noFileSelected")
								: calculateRemainingSize() < 0
								? t("dropZone.exceedSize")
								: ""
						}
					>
						<Button
							variant="contained"
							color="secondary"
							className={`${classes.button} ${classes.btnUpload}`}
							disabled={selectedFiles.length === 0 || calculateRemainingSize() <= 0}
							onClick={handleUpload}
							style={{ pointerEvents: "auto" }}
						>
							{t("dropZone.btnUpload")}
						</Button>
					</Tooltip>
				</div>
			</div>
		</>
	)
}

export default DropZone
